import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Stack,
  Button,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { get } from '../../utils/api';
import {
  FilterButton,
  Stat,
  CloseX,
  ignoreBackdropEscapeClose,
  portalTableContainerSx,
  portalTableHeadCellSx,
  portalTableBodyRowSx,
} from '../../components/portal/portalUi';
import PortalPageHeader from '../../components/portal/PortalPageHeader';

export default function PortalDonationsPage() {
  const { staff } = useAuth();
  const ready = !!staff?.subject;

  const [range, setRange] = React.useState('1y');
  const [orders, setOrders] = React.useState([]);
  const [ordersLoading, setOrdersLoading] = React.useState(false);
  const [ordersError, setOrdersError] = React.useState('');
  const [ordersMeta, setOrdersMeta] = React.useState(null);
  const [orderPage, setOrderPage] = React.useState(1);
  const pageSize = 25;
  const [years, setYears] = React.useState([]);
  const [selectedYear, setSelectedYear] = React.useState('');
  const [productQuery, setProductQuery] = React.useState('');
  const [productOptions, setProductOptions] = React.useState([]);
  const [productLoading, setProductLoading] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [customStart, setCustomStart] = React.useState('');
  const [customEnd, setCustomEnd] = React.useState('');

  const [statsLoading, setStatsLoading] = React.useState(false);
  const [lifetimeTotal, setLifetimeTotal] = React.useState(null);
  const [ytdTotal, setYtdTotal] = React.useState(null);
  const [lastYearTotal, setLastYearTotal] = React.useState(null);
  const [threeYearsTotal, setThreeYearsTotal] = React.useState(null);
  const [lifetimeCount, setLifetimeCount] = React.useState(0);
  const [lifeMemberDialogOpen, setLifeMemberDialogOpen] = React.useState(false);

  const lifeMemberEligible = (Number(threeYearsTotal) || 0) >= 30000;

  const periodTotal = React.useMemo(() => {
    try {
      const completed = (orders || []).filter((o) => String(o.status || '').toLowerCase() === 'completed');
      const sum = completed.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: (orders?.[0]?.currency || 'USD') }).format(sum);
    } catch {
      const completed = (orders || []).filter((o) => String(o.status || '').toLowerCase() === 'completed');
      const sum = completed.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);
      return `$${sum.toFixed(2)}`;
    }
  }, [orders]);

  const lifetimeAvgFmt = React.useMemo(() => {
    try {
      if (!lifetimeCount) return '$0.00';
      const currency = (orders?.[0]?.currency || 'USD');
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((Number(lifetimeTotal || 0)) / lifetimeCount);
    } catch {
      if (!lifetimeCount) return '$0.00';
      const avg = (Number(lifetimeTotal || 0)) / lifetimeCount;
      return `$${avg.toFixed(2)}`;
    }
  }, [lifetimeTotal, lifetimeCount, orders]);

  const sumCompleted = React.useCallback(async ({ range: r = 'all', start, end }) => {
    if (!ready) return 0;
    const per_page = 100;
    let page = 1;
    let total = 0;
    while (true) {
      const params = { status: 'completed', page, per_page };
      if (r === 'custom') { params.start = start; params.end = end; }
      const resp = await get('/portal/orders', params);
      const batch = Array.isArray(resp?.data) ? resp.data : [];
      total += batch.reduce((s, o) => s + (Number(o.total_num) || 0), 0);
      if (batch.length < per_page) break;
      page += 1;
      if (page > 50) break;
    }
    return total;
  }, [ready]);

  const sumCompletedWithCount = React.useCallback(async ({ range: r = 'all', start, end }) => {
    if (!ready) return { total: 0, count: 0 };
    const per_page = 100;
    let page = 1;
    let total = 0;
    let count = 0;
    while (true) {
      const params = { status: 'completed', page, per_page };
      if (r === 'custom') { params.start = start; params.end = end; }
      const resp = await get('/portal/orders', params);
      const batch = Array.isArray(resp?.data) ? resp.data : [];
      total += batch.reduce((s, o) => s + (Number(o.total_num) || 0), 0);
      count += batch.length;
      if (batch.length < per_page) break;
      page += 1;
      if (page > 50) break;
    }
    return { total, count };
  }, [ready]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ready) return;
      setStatsLoading(true);
      try {
        const now = new Date();
        const year = now.getFullYear();
        const startYtd = `${year}-01-01`;
        const endYtd = now.toISOString().slice(0, 10);
        const prevYear = year - 1;
        const startLastYear = `${prevYear}-01-01`;
        const endLastYear = `${prevYear}-12-31`;
        const start3 = `${year - 2}-01-01`;
        const end3 = endYtd;
        const [{ total: lifeTotal, count: lifeCount }, ytd, lastYr, three] = await Promise.all([
          sumCompletedWithCount({ range: 'all' }),
          sumCompleted({ range: 'custom', start: startYtd, end: endYtd }),
          sumCompleted({ range: 'custom', start: startLastYear, end: endLastYear }),
          sumCompleted({ range: 'custom', start: start3, end: end3 }),
        ]);
        if (!cancelled) {
          setLifetimeTotal(lifeTotal);
          setLifetimeCount(lifeCount);
          setYtdTotal(ytd);
          setLastYearTotal(lastYr);
          setThreeYearsTotal(three);
        }
      } catch {
        if (!cancelled) {
          setLifetimeTotal(0); setYtdTotal(0); setLastYearTotal(0); setThreeYearsTotal(0);
        }
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ready, sumCompleted, sumCompletedWithCount]);

  React.useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    (async () => {
      try {
        let page = 1;
        const per_page = 100;
        const yrs = new Set();
        while (page <= 5) {
          const resp = await get('/portal/orders', { page, per_page });
          const batch = Array.isArray(resp?.data) ? resp.data : [];
          for (const o of batch) {
            const d = o.date_created_gmt || o.date_created;
            if (d) yrs.add(String(new Date(d).getFullYear()));
          }
          if (batch.length < per_page) break;
          page += 1;
        }
        if (!cancelled) setYears(Array.from(yrs).sort((a, b) => Number(b) - Number(a)));
      } catch {
        if (!cancelled) setYears([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ready]);

  React.useEffect(() => {
    if (!ready) return;
    if (range === 'custom' && (!customStart || !customEnd)) return;

    let cancelled = false;
    const fetchOrders = async () => {
      setOrdersLoading(true);
      setOrdersError('');
      try {
        const params = {};
        if (range === 'custom') {
          params.start = new Date(`${customStart}T00:00:00.000Z`).toISOString();
          params.end = new Date(`${customEnd}T23:59:59.999Z`).toISOString();
        }
        if (selectedYear) {
          params.start = new Date(`${selectedYear}-01-01T00:00:00.000Z`).toISOString();
          params.end = new Date(`${selectedYear}-12-31T23:59:59.999Z`).toISOString();
        } else if (range === 'custom') {
          params.start = new Date(`${customStart}T00:00:00.000Z`).toISOString();
          params.end = new Date(`${customEnd}T23:59:59.999Z`).toISOString();
        }
        const hasProductSearch = (selectedProduct && selectedProduct.id) || (productQuery && productQuery.trim().length >= 3);
        let endpoint = '/portal/orders';
        if (hasProductSearch) {
          endpoint = '/portal/orders/search';
          delete params.start;
          delete params.end;
          if (selectedProduct && selectedProduct.id) {
            params.product_ids = String(selectedProduct.id);
          } else {
            params.product = productQuery.trim();
          }
        }
        params.page = orderPage;
        params.per_page = pageSize;
        const res = await get(endpoint, params);
        if (cancelled) return;
        const arr = Array.isArray(res?.data) ? res.data : [];
        const mapped = arr.map((o) => ({
          id: o.id,
          status: o.status,
          currency: o.currency || 'USD',
          total: String(o.total_num || 0),
          date_created: o.date_created_gmt || o.date_created,
          billing_first_name: o.billing_first_name,
          billing_last_name: o.billing_last_name,
          billing_email: o.billing_email,
        }));
        const detailed = await Promise.all(mapped.map(async (o) => {
          try {
            const det = await get(`/portal/orders/${o.id}`);
            const line_items = Array.isArray(det?.items) ? det.items.map((it) => ({
              id: it.item_id || it.id,
              name: it.name,
              product_id: it.product_id,
              variation_id: it.variation_id,
              quantity: it.quantity,
              total: String(it.total_num ?? it.price_num ?? 0),
            })) : [];
            return { ...o, line_items };
          } catch {
            return { ...o, line_items: [] };
          }
        }));
        try {
          const productIds = Array.from(new Set(detailed.flatMap((ord) => (ord.line_items || []).map((li) => li.product_id)).filter(Boolean)));
          let variationLabelById = new Map();
          if (productIds.length > 0) {
            const resVars = await get('/portal/facets/variations', { product_id: productIds.join(',') });
            const vars = Array.isArray(resVars?.variations) ? resVars.variations : [];
            variationLabelById = new Map(vars.map((v) => {
              const attrs = Array.isArray(v.attributes) ? v.attributes : [];
              const label = attrs.map((a) => {
                const name = (a.name || a.attribute || '').toString();
                const val = (a.option || a.value || '').toString();
                return name && val ? `${name}: ${val}` : (val || name);
              }).filter(Boolean).join(', ');
              return [v.id, label || (v.sku || '')];
            }));
          }
          for (const ord of detailed) {
            if (Array.isArray(ord.line_items)) {
              ord.line_items = ord.line_items.map((li) => ({
                ...li,
                variation_label: variationLabelById.get(li.variation_id) || '',
              }));
            }
          }
        } catch { /* ignore */ }
        setOrders(detailed);
        setOrdersMeta({ total: res?.meta?.total || detailed.length });
      } catch (e) {
        if (!cancelled) setOrdersError(e?.message || 'Failed to load orders');
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    };
    fetchOrders();
    return () => { cancelled = true; };
  }, [ready, range, customStart, customEnd, selectedYear, selectedProduct, productQuery, orderPage]);

  const loadProductOptions = React.useCallback(async (q) => {
    if (!q || q.trim().length < 3) { setProductOptions([]); return; }
    try {
      setProductLoading(true);
      const res = await get('/portal/facets/products', { q });
      setProductOptions(Array.isArray(res?.products) ? res.products : []);
    } catch {
      setProductOptions([]);
    } finally {
      setProductLoading(false);
    }
  }, []);

  const currencyFmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: (orders?.[0]?.currency || 'USD') });
  const statLifetimeSx = {
    bgcolor: 'rgba(25, 118, 210, 0.07)',
    borderColor: 'rgba(25, 118, 210, 0.18)',
  };
  const statPeriodSx = {
    bgcolor: 'rgba(255, 152, 0, 0.1)',
    borderColor: 'rgba(237, 108, 2, 0.2)',
  };

  return (
    <>
      <PortalPageHeader title="Donations" />
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat label="Transactions Count" value={String(lifetimeCount)} help="Transactions (lifetime)" sx={statLifetimeSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat label="Avg Donation" value={lifetimeAvgFmt} help="Avg per transaction" sx={statLifetimeSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat label="Total Donations" value={currencyFmt.format(lifetimeTotal || 0)} help="All-time" sx={statLifetimeSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat label="This Year" value={currencyFmt.format(ytdTotal || 0)} help="YTD completed" sx={statPeriodSx} />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat
                label="Last Year"
                value={currencyFmt.format(lastYearTotal || 0)}
                help={`Completed donations in ${new Date().getFullYear() - 1}`}
                sx={statPeriodSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2} sx={{ display: 'flex', minWidth: 0 }}>
              <Stat
                label="Last 3 Years"
                value={currencyFmt.format(threeYearsTotal || 0)}
                help="Completed donations in last 3 years"
                sx={statPeriodSx}
              />
            </Grid>
          </Grid>
          {lifeMemberEligible && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 560, lineHeight: 1.5 }}>
              You are eligible for becoming life member of ICCT.{' '}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setLifeMemberDialogOpen(true)}
                sx={{ verticalAlign: 'baseline', fontWeight: 600, cursor: 'pointer' }}
              >
                Click here for more details
              </Link>
              .
            </Typography>
          )}
          {statsLoading && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Updating summary…</Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <FilterButton active={range === 'all'} onClick={() => { setRange('all'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>All</FilterButton>
              <FilterButton active={range === '1y'} onClick={() => { setRange('1y'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>Last 12 mon</FilterButton>
              <FilterButton active={range === 'last_year'} onClick={() => { setRange('last_year'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>Last year</FilterButton>
              <FilterButton active={range === 'ytd'} onClick={() => { setRange('ytd'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>YTD</FilterButton>
              <FilterButton
                active={range === 'custom'}
                onClick={() => {
                  setRange('custom');
                  setSelectedYear('');
                  setSelectedProduct(null);
                  setProductQuery('');
                  if (!customStart || !customEnd) {
                    const today = new Date();
                    const end = today.toISOString().slice(0, 10);
                    const s = new Date(today);
                    s.setDate(today.getDate() - 30);
                    const start = s.toISOString().slice(0, 10);
                    setCustomStart(start);
                    setCustomEnd(end);
                  }
                }}
              >
                Custom
              </FilterButton>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="year-filter-label">Year</InputLabel>
                <Select
                  labelId="year-filter-label"
                  label="Year"
                  value={selectedYear}
                  onChange={(e) => { setSelectedYear(e.target.value); setRange(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {years.map((y) => (<MenuItem key={y} value={y}>{y}</MenuItem>))}
                </Select>
              </FormControl>
              {range === 'custom' && (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">From</Typography>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      style={{ height: 32, padding: '4px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)' }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">To</Typography>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      style={{ height: 32, padding: '4px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)' }}
                    />
                  </Box>
                </Stack>
              )}
            </Stack>
            <Box sx={{ minWidth: 320, maxWidth: 480, flex: 1 }}>
              <Autocomplete
                size="small"
                options={productOptions}
                loading={productLoading}
                value={selectedProduct}
                onChange={(_, v) => { setSelectedProduct(v); setOrderPage(1); }}
                onInputChange={(_, v) => { setProductQuery(v); if ((v || '').trim().length >= 3) loadProductOptions(v); }}
                getOptionLabel={(o) => (o ? `${o.name || ''}${o.sku ? `  ·  ${o.sku}` : ''}` : '')}
                renderInput={(params) => (
                  <TextField {...params} label="Search donations (3+ chars)" placeholder="Type to search donations" />
                )}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
                clearOnBlur={false}
              />
            </Box>
          </Box>

          {ordersLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
              <Box sx={{ textAlign: 'center', '@keyframes pulse': { '0%': { opacity: 0.4 }, '50%': { opacity: 1 }, '100%': { opacity: 0.4 } } }}>
                <CircularProgress size={56} sx={{ mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.5, animation: 'pulse 1.6s ease-in-out infinite' }}>
                  Retrieving orders...
                </Typography>
                <Typography variant="body2" color="text.secondary">Please wait a moment</Typography>
              </Box>
            </Box>
          )}

          {!ordersLoading && (() => {
            const fmt = (d) => {
              try { return new Date(d).toLocaleDateString(); } catch { return null; }
            };
            let period = '';
            const after = ordersMeta?.after || null;
            const before = ordersMeta?.before || null;
            if (range === 'all' || (!after && !before)) {
              period = 'All time';
            } else {
              const from = after ? fmt(after) : '...';
              const to = before ? fmt(before) : fmt(new Date().toISOString());
              period = `${from} to ${to}`;
            }
            return (
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={2} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Orders</Typography>
                  <Typography variant="body2" color="text.secondary">{period}</Typography>
                  <Typography variant="body2" color="text.secondary"> ·  Total contributions: {periodTotal}</Typography>
                </Stack>
                {(ordersMeta?.total > pageSize) && (
                  <Pagination
                    count={Math.ceil((ordersMeta?.total || 0) / pageSize)}
                    page={orderPage}
                    onChange={(_, p) => setOrderPage(p)}
                    size="small"
                  />
                )}
              </Stack>
            );
          })()}
          {ordersError && (
            <Typography variant="body2" color="error">{ordersError}</Typography>
          )}
          {!ordersLoading && !ordersError && (
            orders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No orders found for selected range.</Typography>
            ) : (
              (() => {
                const sorted = [...orders].sort((a, b) => {
                  const da = new Date(a.date_created || a.date_created_gmt || 0).getTime();
                  const db = new Date(b.date_created || b.date_created_gmt || 0).getTime();
                  return db - da;
                });
                const buildItemLabel = (li) => {
                  if (!li?.name) return '';
                  let lineName = li.name.replace(/^Select:\s*/i, '');
                  let variation = li.variation_label || li.variation || '';
                  variation = variation.replace(/^Select:\s*/i, '');
                  if (variation && lineName.includes(variation)) {
                    variation = '';
                  }
                  const qty = Number(li.quantity || 0);
                  const qtyLabel = qty > 1 ? ` ×${qty}` : '';
                  return variation ? `${lineName} — ${variation}${qtyLabel}` : `${lineName}${qtyLabel}`;
                };
                const lowerQ = (productQuery || '').trim().toLowerCase();
                const filtered = (() => {
                  if (selectedProduct && selectedProduct.id) {
                    return sorted.filter((o) => (o.line_items || []).some((li) => String(li.product_id || '') === String(selectedProduct.id)));
                  } if (lowerQ.length >= 3) {
                    return sorted.filter((o) => (o.line_items || []).some((li) =>
                      String(li.name || '').toLowerCase().includes(lowerQ) || String(li.sku || '').toLowerCase().includes(lowerQ)
                    ));
                  }
                  return sorted;
                })();
                const currency = orders?.[0]?.currency || 'USD';
                const fmtTotal = (o) => {
                  try {
                    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(o.total_num ?? o.total ?? 0));
                  } catch {
                    return `$${Number(o.total_num ?? o.total ?? 0).toFixed(2)}`;
                  }
                };
                return (
                  <Box>
                    <TableContainer component={Paper} variant="outlined" sx={portalTableContainerSx}>
                      <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={portalTableHeadCellSx}>Tx #</TableCell>
                            <TableCell sx={portalTableHeadCellSx}>Date</TableCell>
                            <TableCell sx={portalTableHeadCellSx}>Status</TableCell>
                            <TableCell sx={portalTableHeadCellSx}>Product</TableCell>
                            <TableCell align="right" sx={portalTableHeadCellSx}>Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filtered.map((o, idx) => {
                            const pending = String(o.status || '').toLowerCase() === 'pledge-pending';
                            return (
                              <TableRow key={o.id} hover sx={portalTableBodyRowSx(idx)}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{o.id}</TableCell>
                                <TableCell>{new Date(o.date_created || o.date_created_gmt).toLocaleDateString()}</TableCell>
                                <TableCell sx={{ color: pending ? '#800000' : 'inherit' }}>
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{o.status}</Typography>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 360 }}>
                                  {(Array.isArray(o.line_items) ? o.line_items : []).map((li, liIdx) => (
                                    <Typography
                                      key={`${o.id}-li-${li.product_id || 'p'}-${li.variation_id || 'v'}-${liIdx}`}
                                      variant="body2"
                                      sx={{ lineHeight: 1.45 }}
                                    >
                                      {buildItemLabel(li)}
                                    </Typography>
                                  ))}
                                </TableCell>
                                <TableCell align="right">{fmtTotal(o)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {(ordersMeta?.total > pageSize) && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Pagination
                          count={Math.ceil((ordersMeta?.total || 0) / pageSize)}
                          page={orderPage}
                          onChange={(_, p) => setOrderPage(p)}
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                );
              })()
            )
          )}
        </CardContent>
      </Card>

      <Dialog
        open={lifeMemberDialogOpen}
        onClose={(e, reason) => {
          if (ignoreBackdropEscapeClose(e, reason)) return;
          setLifeMemberDialogOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" component="span">Life membership — ICCT</Typography>
          <CloseX onClick={() => setLifeMemberDialogOpen(false)} />
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Eligibility criteria, guidelines, and next steps for life membership will be published here.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Content is coming soon.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setLifeMemberDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
